import { useCallback, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectEndTime,
  selectStartTime,
  setEndTime,
  setStartTime,
} from 'state/workflowSlice';

import { TimeString } from 'appTypes';
import DateTimePickerWrapper from 'features/common/DateTimeSelector/DateTimePickerWrapper';
import { TimeSelector } from 'features/common/DateTimeSelector/TimeSelector';
import {
  DEFAULT_HOURS,
  DEFAULT_TIME,
  DEFAULT_TIME_WITH_MERIDIEM,
  convertTo12Hour,
  convertTo24HourString,
  diffHours,
} from 'features/common/DateTimeSelector/utils';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import StlConfirmButtons from 'features/common/StlConfirmButtons';
import StlPopoverWrapper from 'features/common/StlPopoverWrapper/StlPopoverWrapper';

import styles from './ODMatrix.module.css';

const HOUR_LIMIT = 24;
function midnightToMidnightAfter(inp: string) {
  if (inp === '00:00') {
    inp = '24:00';
  }
  return inp;
}
export function normalizeEndTime(inp: TimeString): TimeString {
  if (inp === '24:00') return '00:00';
  return inp;
}
export default function ODTimeSelector() {
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState<boolean>(false);
  const [showBtns, setShowBtns] = useState<boolean>(true);
  const [initialValues, setInitialValues] = useState<
    { startTime: TimeString; endTime: TimeString } | undefined
  >(undefined);

  const startTime = useAppSelector(selectStartTime);
  const endTime = useAppSelector(selectEndTime);
  const [hours, setHours] = useState<number>(
    (startTime && endTime && diffHours(startTime, endTime)) || DEFAULT_HOURS,
  );
  const [tempRange, setTempRange] = useState({
    startTime,
    endTime: normalizeEndTime(endTime),
  });
  const txtIsPlaceholder = !tempRange?.endTime || !tempRange?.startTime;

  const getButtonTitle = useCallback(() => {
    if (txtIsPlaceholder) return 'E.g., 12:00 AM to 12:00 PM';
    return `${convertTo12Hour(tempRange.startTime)} to ${convertTo12Hour(tempRange.endTime)}`;
  }, [tempRange, txtIsPlaceholder]);

  useEffect(() => {
    if (!startTime) dispatch(setStartTime(DEFAULT_TIME));
    if (!endTime) dispatch(setEndTime(DEFAULT_TIME));
  }, []);

  useEffect(() => {
    if (open && !initialValues && startTime && endTime) {
      const normalizedEnd = normalizeEndTime(endTime);
      setInitialValues({ startTime, endTime: normalizedEnd });
      setTempRange({ startTime, endTime: normalizedEnd });
      setHours(diffHours(startTime, normalizedEnd) || DEFAULT_HOURS);
    } else if (!open) setInitialValues(undefined);
  }, [open, startTime, endTime]);

  const onShowHoursChange = (val: boolean) => {
    setShowBtns(!val);
  };

  const handleApply = () => {
    dispatch(setStartTime(tempRange.startTime));
    dispatch(setEndTime(midnightToMidnightAfter(tempRange.endTime)));
    setOpen(false);
  };

  const handleCancel = () => {
    if (initialValues) {
      setTempRange({
        startTime: initialValues.startTime,
        endTime: initialValues.endTime,
      });
      setHours(
        diffHours(initialValues.startTime, initialValues.endTime) ||
          DEFAULT_HOURS,
      );
    }
    setOpen(false);
  };

  const setStartHour = (val: string) => {
    setTempRange((prev) => ({
      ...prev,
      startTime: convertTo24HourString(val),
    }));
  };

  const setEndHour = (val: string) => {
    const newEndTime = convertTo24HourString(val) as TimeString;
    if (newEndTime !== tempRange?.endTime) {
      setTempRange((prev) => ({
        ...prev,
        endTime: newEndTime,
      }));
      if (tempRange?.startTime) {
        setHours(diffHours(tempRange.startTime, newEndTime));
      }
    }
  };
  const startHour = tempRange?.startTime
    ? convertTo12Hour(tempRange.startTime)
    : DEFAULT_TIME_WITH_MERIDIEM;

  const endHour = tempRange?.endTime
    ? convertTo12Hour(tempRange.endTime)
    : DEFAULT_TIME_WITH_MERIDIEM;

  return (
    <StlPopoverWrapper
      open={open}
      setOpen={setOpen}
      startIcon={<FontAwesomeIcon name="clock" type="regular" />}
      disabled={false}
      narrow
      renderLabel={getButtonTitle}
      isPlaceholderText={txtIsPlaceholder}
      buttonClass={styles.selector}
      onClose={handleApply}
    >
      <DateTimePickerWrapper
        height="auto"
        width="236px"
        title="Select time"
        component={
          <TimeSelector
            onShowHoursChange={onShowHoursChange}
            canEditEndTime
            minBins
            amPmVersion
            maxHours={HOUR_LIMIT}
            startHour={startHour}
            setStartHour={setStartHour}
            endHour={endHour}
            setEndHour={setEndHour}
            hours={hours}
            setHours={setHours}
          />
        }
      />
      {showBtns && (
        <StlConfirmButtons
          handleApply={handleApply}
          handleCancel={handleCancel}
        />
      )}
    </StlPopoverWrapper>
  );
}
